import React, { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react'

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface NotificationData {
  id: string
  type: NotificationType
  title: string
  message?: string
  duration?: number
}

interface NotificationProps {
  notification: NotificationData
  onRemove: (id: string) => void
}

export function Notification({ notification, onRemove }: NotificationProps) {
  const { id, type, title, message, duration = 3000 } = notification

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onRemove(id)
      }, duration)

      return () => clearTimeout(timer)
    }
  }, [id, duration, onRemove])

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-primary dark:text-primary" />
      case 'error':
        return <XCircle className="h-5 w-5 text-destructive dark:text-red-400" />
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
      case 'info':
        return <Info className="h-5 w-5 text-blue-500 dark:text-blue-400" />
      default:
        return <Info className="h-5 w-5 text-blue-500 dark:text-blue-400" />
    }
  }

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-primary/10 border-primary/30 dark:bg-black dark:border-primary/50'
      case 'error':
        return 'bg-destructive/10 border-destructive/20 dark:bg-black dark:border-destructive'
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 dark:bg-black dark:border-yellow-600'
      case 'info':
        return 'bg-blue-50 border-blue-200 dark:bg-black dark:border-blue-600'
      default:
        return 'bg-blue-50 border-blue-200 dark:bg-black dark:border-blue-600'
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 300, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 300, scale: 0.95 }}
      className={`p-4 rounded-lg border ${getBackgroundColor()} shadow-lg`}
    >
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
          {message && (
            <p className="text-sm text-muted-foreground mt-1">{message}</p>
          )}
        </div>
        <button
          onClick={() => onRemove(id)}
          className="p-1 hover:bg-white/10 dark:hover:bg-white/20 rounded transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  )
}

interface NotificationContainerProps {
  notifications: NotificationData[]
  onRemove: (id: string) => void
}

export function NotificationContainer({ notifications, onRemove }: NotificationContainerProps) {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      <AnimatePresence>
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            notification={notification}
            onRemove={onRemove}
          />
        ))}
      </AnimatePresence>
    </div>
  )
}
